// Mock data for IT Software Services Website

export const heroData = {
  title: "Turn AI Strategy Into Business Results",
  subtitle: "We deliver end-to-end AI transformation solutions that drive measurable ROI and competitive advantage for forward-thinking enterprises",
  cta: "Get a Consultation",
  image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
};

export const servicesData = [
  {
    id: 1,
    title: "AI Strategy & Roadmap Development",
    icon: "Lightbulb",
    shortDesc: "Transform your business vision into actionable AI strategies",
    fullDesc: "We help you develop comprehensive AI strategies aligned with your business goals. Our team assesses your current capabilities, identifies high-impact AI opportunities, and creates a practical roadmap for AI adoption that delivers measurable ROI.",
    benefits: [
      "Custom AI strategy aligned with business objectives",
      "Prioritized use case identification and validation",
      "Technology assessment and vendor selection",
      "Change management and adoption planning"
    ],
    useCases: [
      "Enterprise AI transformation roadmaps",
      "AI maturity assessment and gap analysis",
      "Pilot program design and validation",
      "AI governance framework development"
    ],
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c"
  },
  {
    id: 2,
    title: "Generative AI Solutions",
    icon: "Sparkles",
    shortDesc: "Harness the power of GenAI to automate and innovate",
    fullDesc: "Implement cutting-edge Generative AI solutions that transform how your business operates. From intelligent chatbots to content generation and document processing, we build custom GenAI applications that drive efficiency and create new value.",
    benefits: [
      "Custom LLM-powered applications",
      "Automated content and document generation",
      "Intelligent process automation",
      "RAG systems for enterprise knowledge"
    ],
    useCases: [
      "AI-powered customer service chatbots",
      "Automated report and content generation",
      "Intelligent document processing and summarization",
      "Knowledge management and Q&A systems"
    ],
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995"
  },
  {
    id: 3,
    title: "Machine Learning & Predictive Analytics",
    icon: "TrendingUp",
    shortDesc: "Unlock predictive insights to drive smarter decisions",
    fullDesc: "Deploy advanced machine learning models that predict outcomes, identify patterns, and automate decision-making. Our ML solutions help you forecast demand, detect anomalies, optimize operations, and personalize customer experiences at scale.",
    benefits: [
      "Custom ML model development and deployment",
      "Predictive forecasting and trend analysis",
      "Anomaly detection and risk assessment",
      "Recommendation engines and personalization"
    ],
    useCases: [
      "Demand forecasting and inventory optimization",
      "Predictive maintenance for equipment",
      "Customer churn prediction and retention",
      "Fraud detection and risk scoring"
    ],
    image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86"
  },
  {
    id: 4,
    title: "AI-Powered Automation",
    icon: "Zap",
    shortDesc: "Automate workflows and boost productivity with AI",
    fullDesc: "Transform repetitive tasks into automated workflows powered by AI. We implement intelligent automation solutions that combine RPA, AI, and ML to streamline operations, reduce costs, and free your team to focus on high-value work.",
    benefits: [
      "End-to-end process automation",
      "Intelligent document processing (IDP)",
      "Workflow orchestration and optimization",
      "24/7 automated operations"
    ],
    useCases: [
      "Invoice and document processing automation",
      "Customer onboarding and KYC automation",
      "Data entry and validation automation",
      "Automated reporting and alerts"
    ],
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e"
  },
  {
    id: 5,
    title: "AI Model Training & Fine-tuning",
    icon: "Settings",
    shortDesc: "Optimize AI models for your specific business needs",
    fullDesc: "Customize and fine-tune AI models to achieve superior performance on your unique datasets and use cases. We handle the entire ML lifecycle from data preparation to model training, evaluation, and continuous improvement.",
    benefits: [
      "Domain-specific model customization",
      "Transfer learning and fine-tuning",
      "Model performance optimization",
      "Continuous monitoring and retraining"
    ],
    useCases: [
      "Custom NLP models for industry terminology",
      "Computer vision models for specific products",
      "Sentiment analysis for brand monitoring",
      "Named entity recognition for documents"
    ],
    image: "https://images.unsplash.com/photo-1527474305487-b87b222841cc"
  },
  {
    id: 6,
    title: "AI Implementation & Integration",
    icon: "Clipboard",
    shortDesc: "Seamlessly integrate AI into your existing systems",
    fullDesc: "We ensure smooth integration of AI solutions into your existing technology infrastructure. Our implementation services include API development, system integration, deployment, and ongoing support to maximize AI adoption and ROI.",
    benefits: [
      "Seamless integration with existing systems",
      "Scalable cloud-based deployment",
      "API development and management",
      "Change management and training"
    ],
    useCases: [
      "AI integration with CRM and ERP systems",
      "API-first AI service deployment",
      "Legacy system modernization with AI",
      "Multi-cloud AI infrastructure setup"
    ],
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
  }
];

export const whyChooseUsData = [
  {
    icon: "Award",
    title: "AI-First Approach",
    description: "Proven track record delivering production-ready AI solutions that drive measurable business impact"
  },
  {
    icon: "Users",
    title: "Expert AI Team",
    description: "ML engineers, AI architects, and domain experts specializing in GenAI, LLMs, and enterprise AI"
  },
  {
    icon: "Zap",
    title: "Rapid Implementation",
    description: "From strategy to production in weeks, not months—delivering quick wins and long-term value"
  },
  {
    icon: "Shield",
    title: "Enterprise-Grade AI",
    description: "Secure, scalable, and compliant AI solutions built for mission-critical business operations"
  }
];

export const industriesData = [
  {
    id: 1,
    name: "Financial Services",
    icon: "DollarSign",
    description: "AI-powered risk management, fraud detection, and intelligent automation",
    problem: "Manual document processing and compliance checks causing delays, high costs, and regulatory risks in loan approvals and customer onboarding",
    solution: "Deployed GenAI-powered document intelligence and automated risk assessment system with ML-based fraud detection, reducing processing time by 85% while improving accuracy",
    results: "95% fraud detection accuracy, 85% faster processing, 65% reduction in operational costs"
  },
  {
    id: 2,
    name: "Healthcare",
    icon: "HeartPulse",
    description: "AI-driven diagnostics, patient care optimization, and clinical automation",
    problem: "Overwhelmed clinical staff spending 40% of time on documentation and struggling to identify high-risk patients early",
    solution: "Implemented AI-powered clinical documentation assistant and predictive analytics for early intervention, using NLP to automate note-taking and ML models for risk stratification",
    results: "50% reduction in documentation time, 35% fewer readmissions, improved patient outcomes"
  },
  {
    id: 3,
    name: "Retail & E-commerce",
    icon: "ShoppingCart",
    description: "AI personalization, demand forecasting, and customer service automation",
    problem: "Low conversion rates due to generic customer experiences and inability to predict demand accurately, leading to stockouts and excess inventory",
    solution: "Built AI recommendation engine and demand forecasting system using collaborative filtering and time-series ML models, plus GenAI chatbot for 24/7 customer support",
    results: "40% increase in conversion rate, 65% reduction in stockouts, 70% queries automated"
  },
  {
    id: 4,
    name: "Manufacturing",
    icon: "Factory",
    description: "Predictive maintenance, quality control AI, and production optimization",
    problem: "Unexpected equipment failures causing significant annual losses and manual quality inspections missing 20% of defects",
    solution: "Deployed IoT sensors with ML predictive maintenance models and computer vision for automated quality inspection, predicting failures 3 weeks in advance",
    results: "80% reduction in downtime, 95% defect detection accuracy, 70% savings in maintenance costs"
  },
  {
    id: 5,
    name: "Insurance",
    icon: "Shield",
    description: "Claims automation, risk assessment AI, and customer service transformation",
    problem: "Claims processing taking 15-20 days with high operational costs and poor customer satisfaction due to manual review processes",
    solution: "Implemented end-to-end AI claims processing with document understanding, damage assessment using computer vision, and automated fraud detection",
    results: "Claims processed in 2 hours vs 15 days, 90% automation rate, 60% reduction in operational costs"
  },
  {
    id: 6,
    name: "Professional Services",
    icon: "Briefcase",
    description: "Knowledge management AI, proposal automation, and client insights",
    problem: "Teams spending 60% of time searching for information and creating proposals from scratch, limiting billable hours and growth",
    solution: "Built AI-powered knowledge management system with RAG technology and GenAI proposal generator, enabling instant access to past work and automated document creation",
    results: "75% reduction in proposal creation time, 45% increase in billable hours, faster decision-making"
  }
];

export const caseStudiesData = [
  {
    id: 1,
    title: "E-commerce Giant Deploys AI-Powered Customer Service",
    client: "Leading Online Retailer",
    industry: "E-commerce",
    challenge: "A major e-commerce platform was overwhelmed with 50,000+ daily customer inquiries, resulting in long wait times, high support costs, and declining customer satisfaction. Traditional chatbots provided poor experiences with low resolution rates.",
    solution: "We implemented an advanced GenAI-powered customer service system using fine-tuned large language models. The system handled complex queries, integrated with order management systems, and seamlessly escalated to human agents when needed. We also built a comprehensive knowledge base using RAG technology.",
    results: [
      "70% of queries resolved by AI without human intervention",
      "60% reduction in average response time",
      "65% reduction in support operational costs",
      "Customer satisfaction scores improved by 45%"
    ],
    technologies: ["GPT-4", "LangChain", "Vector DB", "AWS Lambda"],
    image: "https://images.unsplash.com/photo-1576267423048-15c0040fec78"
  },
  {
    id: 2,
    title: "Manufacturing Leader Implements Predictive Maintenance",
    client: "Global Manufacturing Corporation",
    industry: "Manufacturing",
    challenge: "A manufacturing company faced frequent unexpected equipment failures causing costly production downtime. Reactive maintenance was inefficient, and scheduled maintenance was often premature or too late, resulting in significant annual losses.",
    solution: "We deployed an AI-powered predictive maintenance system that analyzed sensor data, historical maintenance records, and operational parameters using advanced machine learning models. The system predicted equipment failures 2-4 weeks in advance with high accuracy.",
    results: [
      "85% reduction in unexpected equipment failures",
      "40% decrease in maintenance costs",
      "75% reduction in downtime-related losses",
      "Equipment lifespan extended by 25%"
    ],
    technologies: ["Python", "TensorFlow", "IoT Sensors", "Azure ML"],
    image: "https://images.pexels.com/photos/5257005/pexels-photo-5257005.jpeg"
  },
  {
    id: 3,
    title: "Financial Services Automates Document Processing",
    client: "Regional Insurance Provider",
    industry: "Insurance",
    challenge: "An insurance company processed 100,000+ documents monthly through manual data entry, leading to high costs, processing delays of 7-10 days, and frequent errors affecting customer onboarding and claims processing.",
    solution: "We built an intelligent document processing system using computer vision and NLP. The AI solution automatically extracted, classified, and validated data from various document types including applications, claims, and medical records, with human-in-the-loop for edge cases.",
    results: [
      "92% automation rate for document processing",
      "Processing time reduced from 7 days to 4 hours",
      "70% reduction in operational costs",
      "98% data accuracy with minimal human review"
    ],
    technologies: ["GPT-4 Vision", "Azure Document Intelligence", "Python", "FastAPI"],
    image: "https://images.unsplash.com/photo-1739298061740-5ed03045b280"
  }
];

export const blogPostsData = [
  {
    id: 1,
    title: "The Future of Data Analytics in 2025",
    excerpt: "Explore emerging trends in data analytics including AI-powered insights, real-time processing, and democratization of data across organizations.",
    author: "Sarah Johnson",
    date: "December 15, 2024",
    readTime: "5 min read",
    category: "Data Analytics",
    image: "https://images.pexels.com/photos/7691673/pexels-photo-7691673.jpeg"
  },
  {
    id: 2,
    title: "Building Scalable Data Pipelines: Best Practices",
    excerpt: "Learn the key principles and patterns for designing data pipelines that can handle growing data volumes while maintaining reliability.",
    author: "Michael Chen",
    date: "December 10, 2024",
    readTime: "7 min read",
    category: "Data Engineering",
    image: "https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg"
  },
  {
    id: 3,
    title: "How AI is Transforming Business Intelligence",
    excerpt: "Discover how artificial intelligence and machine learning are revolutionizing the way businesses extract insights from their data.",
    author: "Dr. Emily Rodriguez",
    date: "December 5, 2024",
    readTime: "6 min read",
    category: "Artificial Intelligence",
    image: "https://images.pexels.com/photos/6694560/pexels-photo-6694560.jpeg"
  },
  {
    id: 4,
    title: "Cloud Migration Strategies for Data-Heavy Applications",
    excerpt: "A comprehensive guide to migrating your data infrastructure to the cloud while ensuring security, performance, and cost-effectiveness.",
    author: "David Kumar",
    date: "November 28, 2024",
    readTime: "8 min read",
    category: "Cloud Infrastructure",
    image: "https://images.unsplash.com/photo-1489436969537-cf0c1dc69cba"
  }
];

export const teamData = [
  {
    id: 1,
    name: "John Anderson",
    role: "CEO & Founder",
    bio: "15+ years in enterprise software and data solutions",
    image: "https://images.pexels.com/photos/7691694/pexels-photo-7691694.jpeg"
  },
  {
    id: 2,
    name: "Sarah Mitchell",
    role: "Chief Data Officer",
    bio: "Expert in data strategy and business intelligence",
    image: "https://images.pexels.com/photos/7691694/pexels-photo-7691694.jpeg"
  },
  {
    id: 3,
    name: "Michael Torres",
    role: "VP of Engineering",
    bio: "Leading development teams building scalable solutions",
    image: "https://images.pexels.com/photos/7691694/pexels-photo-7691694.jpeg"
  },
  {
    id: 4,
    name: "Emily Chen",
    role: "Head of Analytics",
    bio: "Specializing in predictive modeling and ML",
    image: "https://images.pexels.com/photos/7691694/pexels-photo-7691694.jpeg"
  }
];

export const testimonialsData = [
  {
    id: 1,
    text: "Their data analytics solution transformed how we make decisions. We now have real-time insights that have directly improved our bottom line.",
    author: "Robert Martinez",
    position: "CFO, TechCorp Industries",
    company: "TechCorp"
  },
  {
    id: 2,
    text: "The custom software they built for us scaled perfectly as we grew from 50 to 500 employees. Outstanding technical expertise and support.",
    author: "Jennifer Lee",
    position: "COO, GrowthStart",
    company: "GrowthStart"
  },
  {
    id: 3,
    text: "Best decision we made was partnering with them for our BI implementation. The dashboards are intuitive and have empowered our entire team.",
    author: "David Kumar",
    position: "Director of Operations, RetailHub",
    company: "RetailHub"
  }
];

export const clientLogos = [
  { name: "TechCorp", logo: "TC" },
  { name: "GrowthStart", logo: "GS" },
  { name: "RetailHub", logo: "RH" },
  { name: "FinanceFirst", logo: "FF" },
  { name: "HealthPlus", logo: "HP" },
  { name: "LogiTech", logo: "LT" }
];

export const companyInfo = {
  mission: "To empower businesses with data-driven software solutions that unlock growth, efficiency, and competitive advantage.",
  vision: "A world where every business, regardless of size, can leverage the power of data and technology to achieve their goals.",
  values: [
    {
      title: "Innovation",
      description: "We stay at the forefront of technology, constantly exploring new ways to solve complex problems."
    },
    {
      title: "Excellence",
      description: "We are committed to delivering high-quality solutions that exceed expectations."
    },
    {
      title: "Collaboration",
      description: "We work closely with our clients as partners, ensuring success through open communication."
    },
    {
      title: "Integrity",
      description: "We conduct business with honesty, transparency, and respect for all stakeholders."
    }
  ],
  story: "Founded in 2015, we started with a simple belief: that data should be accessible and actionable for every business. What began as a small team of data enthusiasts has grown into a full-service software and analytics firm serving clients across industries. Today, we combine deep technical expertise with business acumen to deliver solutions that drive real results."
};

export const contactInfo = {
  email: "info@k-protechnologies.com",
  phone: "+91 9819647270",
  address: "2102 Spaces, Inspire Hub, Adani Western Heights, J.P Road, Andheri west, Mumbai 400053, India",
  hours: "Monday - Friday: 9:00 AM - 6:00 PM IST"
};
